import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import Layout4 from "../../DataLayouts/Layout4";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { operatorRequiresValue } from "../../../Components/filters/operators";

function ProjectMileStoneList() {
  const navigate = useNavigate();
  const { userData } = useAuthStore();
  const orgId =
    userData?.organization?.organization_id ??
    userData?.user?.organization_id ??
    userData?.organization_id ??
    null;
  const [searchParams, setSearchParams] = useSearchParams();

  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationData, setPaginationData] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNext: false,
    hasPrev: false,
  });

  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(true);

  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const ViewFunction = (obj) => {
    navigate(`/project-milestone/view/${obj?.id}`);
  };

  useEffect(() => {
    if (hasInitializedURL.current) return;

    const currentParams = new URLSearchParams(searchParams);
    let needsUpdate = false;

    if (!currentParams.has("page")) {
      currentParams.set("page", "1");
      needsUpdate = true;
    }
    if (!currentParams.has("pageSize")) {
      currentParams.set("pageSize", "10");
      needsUpdate = true;
    }
    if (!currentParams.has("sortBy")) {
      currentParams.set("sortBy", "project_milestone_name");
      needsUpdate = true;
    }
    if (!currentParams.has("sortOrder")) {
      currentParams.set("sortOrder", "asc");
      needsUpdate = true;
    }

    if (needsUpdate) {
      setSearchParams(currentParams, { replace: true });
    }

    hasInitializedURL.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!orgId) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `Project_milestone_grid_${orgId}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig?.datagrid_default_configuration) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);
            if (Array.isArray(serverCfg.columns) && serverCfg.columns.length > 0) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(null);
            }
          } else {
            setConfigColumns(null);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(null);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [orgId]);

  const fetchMilestonesWithFilters = useCallback(async () => {
    if (!orgId) return;

    if (fetchControllerRef.current) {
      fetchControllerRef.current.abort();
    }
    fetchControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const filters = [];
      const processedFields = new Set();

      searchParams.forEach((value, key) => {
        if (
          key.endsWith("_operator") ||
          key.endsWith("_logic") ||
          key.endsWith("_label")
        )
          return;

        if (key.startsWith("filter_")) {
          const field = key.replace("filter_", "");
          if (processedFields.has(field)) return;
          processedFields.add(field);

          const operatorKey = `filter_${field}_operator`;
          const operator = searchParams.get(operatorKey) || "contains";

          const requiresValue = operatorRequiresValue(operator);
          if (!requiresValue || value) {
            let filterValue = requiresValue ? value : null;

            if (requiresValue && filterValue) {
              if (operator === "equals" || operator === "not_equals") {
                filterValue = filterValue
                  .split(",")
                  .map((v) => v.trim().toLowerCase());
              } else if (operator === "between" || operator === "not_between") {
                filterValue = filterValue.split(",").map((v) => v.trim());
              }
            }

            filters.push({ field, operator, value: filterValue });
          }
        }
      });

      const search = searchParams.get("search");

      const requestBody = {
        organization_id: orgId,
        page: Number.parseInt(searchParams.get("page"), 10) || 1,
        pageSize: Math.min(
          Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          100
        ),
        sort: {
          field: searchParams.get("sortBy") || "project_milestone_name",
          direction: searchParams.get("sortOrder") || "asc",
        },
        filters,
        ...(search && search.trim() !== "" && { search: search.trim() }),
      };

      const response = await fetch(
        `${MAIN_URL}/api/project-management/project-milestone/filtered`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
          signal: fetchControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && Array.isArray(result.data)) {
        const normalized = result.data.map((item) => {
          const rawProjectId =
            item.organization_project_id ?? item.project_id ?? null;

          const projectLabel =
            item.project ??
            item.project_name ??
            (typeof item.organization_project === "string"
              ? item.organization_project
              : null) ??
            (typeof rawProjectId === "string" ? rawProjectId : null) ??
            "-";

          return {
            ...item,
            id: item.organization_project_milestone_id ?? item.id,
            is_active:
              item.is_active === 1 ||
              item.is_active === true ||
              item.is_active === "1",
            is_billable:
              item.is_billable === 1 ||
              item.is_billable === true ||
              item.is_billable === "1",

            project: projectLabel,
            project_name: projectLabel,

            // Back-compat for saved datagrid configs that bind to the FK key directly.
            organization_project_id: projectLabel,
          };
        });
        setMilestones(normalized);

        if (result.pagination) {
          const pageSize =
            result.pagination.pageSize || result.pagination.limit || 10;
          const requestedPage =
            Number.parseInt(searchParams.get("page"), 10) || 1;
          setPaginationData({
            page: result.pagination.page || requestedPage,
            pageSize,
            total: result.pagination.total || 0,
            totalPages: result.pagination.totalPages || 1,
            hasNext: result.pagination.hasNext || false,
            hasPrev: result.pagination.hasPrev || false,
          });
        } else {
          setPaginationData({
            page: 1,
            pageSize: 10,
            total: normalized.length,
            totalPages: 1,
            hasNext: false,
            hasPrev: false,
          });
        }
      } else {
        setMilestones([]);
        setPaginationData({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        });
        toast.error(
          result.message || result.error || "Failed to fetch project milestones"
        );
      }
    } catch (err) {
      if (err.name === "AbortError") return;
      console.error("Error fetching project milestones:", err);
      toast.error("Failed to fetch project milestones");
      setMilestones([]);
      setPaginationData({
        page: 1,
        pageSize: 10,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false,
      });
    } finally {
      setLoading(false);
    }
  }, [orgId, searchParams]);

  useEffect(() => {
    fetchMilestonesWithFilters();
  }, [fetchMilestonesWithFilters]);

  return (
    <>
      <Layout4
        heading="Project MileStones"
        Route="/project-milestone"
        btnName="Add Project MileStones"
        showAddButton={true}
        add_action="ADD_PROJECT_MIESTONE"
      />

      {loading || loadingConfig ? (
        <div style={{ padding: 16 }}>Loading...</div>
      ) : (
        <TableDataGeneric
          viewFunction={(obj) => ViewFunction(obj)}
          gridKey="Project_milestone_grid"
          tableName="organization_project_milestones"
          filterModule="organization_project_milestones"
          primaryKey="id"
          heading="organization_project_milestones"
          data={milestones}
          sortname={"project_milestone_name"}
          showActions={true}
          Route="/project-milestone"
          token={localStorage.getItem("token")}
          configss={configColumns}
          edit_delete_action={[
            "EDIT_PROJECT_MILESTONES",
            "DELETE_PROJECT_MILESTONE",
          ]}
          paginationData={paginationData}
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default ProjectMileStoneList;
